import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import CopyIcon from '@mui/icons-material/FileCopy'
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  DataGrid,
  GridCellParams,
  GridRenderCellParams,
  GridColumnVisibilityModel,
  GridFilterModel,
  GridSortModel,
  useGridApiRef,
} from '@mui/x-data-grid'
import { useSnackbar } from 'notistack'
import React, { FC, useEffect, useState } from 'react'
import { connect } from 'react-redux'
import {
  DtoolRelationType,
  HardwareType,
  MaterialGroupPropsType,
  MaterialGroupType,
  MaterialType,
} from '../../api/appAPI'
import { saveGridStateInLocalStorage } from '../../common/helpers'
import {
  addMaterialGroup,
  copyMaterialGroup,
  deleteMaterialGroup,
  editMaterialGroup,
} from '../../redux/itemReducer'
import { StateType } from '../../redux/store'
import { EnqueueSnackbarType, NearTooltip } from '../common/Helpers'
import { actions as appActions } from './../../redux/appReducer'
import DefineMaterialGroup from './DefineMaterialGroup'
import EditMaterialGroup from './EditMaterialGroup'

interface IProps {
  hardwaresList: HardwareType[]
  materials: MaterialType[]
  materialGroups: MaterialGroupType[]
  materialGroupGridVisibilityModel: GridColumnVisibilityModel | undefined
  materialGroupGridSortModel: GridSortModel | undefined
  materialGroupGridFilterModel: GridFilterModel | undefined
  activeMaterialGroupId: number
  dtoolRelationsList: DtoolRelationType[]

  addMaterialGroup(
    mGroupProps: MaterialGroupPropsType,
    enqueueSnackbar: EnqueueSnackbarType
  ): void
  editMaterialGroup(
    id: number,
    mGroup: MaterialGroupPropsType,
    enqueueSnackbar: EnqueueSnackbarType
  ): void
  deleteMaterialGroup(id: number, enqueueSnackbar: EnqueueSnackbarType): void
  copyMaterialGroup(id: number, enqueueSnackbar: EnqueueSnackbarType): void
  setMaterialGroupGridVisibilityModel(
    model: GridColumnVisibilityModel | undefined
  ): void
  setMaterialGroupGridSortModel(model: GridSortModel | undefined): void
  setMaterialGroupGridFilterModel(model: GridFilterModel | undefined): void
  setActiveMaterialGroupId(value: number): void
  openMaterialDetails(id: number): void
  openHardwareDetails(id: number): void
}

const MaterialGroupTab: FC<IProps> = (props) => {
  const [activeMGroup, setActiveMGroup] = useState<
    undefined | MaterialGroupType
  >(props.materialGroups.find((m) => m.id === props.activeMaterialGroupId))
  const apiRef = useGridApiRef()
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deleteID, setDeleteID] = useState<null | number>(null)
  const [ifSureCheckbox, setIfSureCheckbox] = useState(false)
  const [usedMGroupsInDtools, setUsedMGroupsInDtools] = useState<
    { id: number; tooltip: string }[]
  >([])
  const [selectedTags, setSelectedTags] = useState<string[]>([])

  const allTags = Array.from(
    new Set(
      props.dtoolRelationsList
        .filter((r) => r.type === 'materialGroup')
        .flatMap((r) => r.tags)
    )
  ).sort()

  const { enqueueSnackbar } = useSnackbar()

  const handleCopyMaterialGroupClick = (id: number) => {
    props.copyMaterialGroup(id, enqueueSnackbar)
  }

  const handleDeleteMaterialClick = (id: number) => {
    setDeleteID(id)
    setDeleteDialogOpen(true)
  }

  const handleDeleteDialogClose = () => {
    setDeleteID(null)
    setDeleteDialogOpen(false)
  }

  const handleDeleteDialogDeleteClick = () => {
    if (deleteID) props.deleteMaterialGroup(deleteID, enqueueSnackbar)
    handleDeleteDialogClose()
  }

  useEffect(() => {
    setActiveMGroup(
      props.materialGroups.find((m) => m.id === props.activeMaterialGroupId)
    )
  }, [props.activeMaterialGroupId, props.materialGroups])

  useEffect(() => {
    if (activeMGroup && props.dtoolRelationsList) {
      setTimeout(() => {
        try {
          const rowIndex = apiRef.current.getRowIndexRelativeToVisibleRows(
            activeMGroup.id
          )
          if (rowIndex !== undefined && rowIndex !== -1) {
            apiRef.current.scrollToIndexes({ rowIndex })
          }
        } catch (e) {
          // apiRef might not be fully initialized or row not found
        }
      }, 100)
    }
  }, [activeMGroup, props.dtoolRelationsList, apiRef])

  useEffect(() => {
    saveGridStateInLocalStorage(
      {
        visibilityModel: props.materialGroupGridVisibilityModel,
        filterModel: props.materialGroupGridFilterModel,
        sortModel: props.materialGroupGridSortModel,
      },
      'materialGroup'
    )
  }, [
    props.materialGroupGridVisibilityModel,
    props.materialGroupGridFilterModel,
    props.materialGroupGridSortModel,
  ])

  useEffect(() => {
    const arr = []
    const mgIds = props.materialGroups.map((m) => m.id)
    for (const dtoolR of props.dtoolRelationsList.filter(
      (r) => r.type === 'materialGroup'
    )) {
      if (mgIds.includes(dtoolR.targetId)) {
        arr.push({
          id: dtoolR.targetId,
          tooltip: `Material Group is used in Dtool: ${dtoolR.tags.join()}`,
        })
      }
    }
    setUsedMGroupsInDtools(arr)
  }, [props.dtoolRelationsList, props.materialGroups])

  return (
    <Box display="flex" style={{ height: window.innerHeight - 48 }}>
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        pt={2}
        style={{ borderRight: 'solid 1px black', flexGrow: 1 }}
      >
        <Typography variant="h6">Material Groups List</Typography>

        {allTags.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              my: 1,
              justifyContent: 'center',
              width: '90%',
            }}
          >
            {allTags.map((tag) => (
              <Chip
                key={tag}
                label={tag}
                color={selectedTags.includes(tag) ? 'primary' : 'default'}
                onClick={() => {
                  if (selectedTags.includes(tag)) {
                    setSelectedTags(selectedTags.filter((t) => t !== tag))
                  } else {
                    setSelectedTags([...selectedTags, tag])
                  }
                }}
              />
            ))}
          </Box>
        )}

        <Paper style={{ width: '90%', maxHeight: '90%', overflowY: 'auto' }}>
          <List component="nav" style={{ padding: 0 }}>
            <ListItem
              button
              onClick={() => {
                props.setActiveMaterialGroupId(0)
              }}
            >
              <ListItemText primary="New Material Group" />
              <AddIcon />
            </ListItem>
            <Divider />
          </List>
        </Paper>
        <DataGrid
          apiRef={apiRef}
          style={{ width: '90%', maxHeight: '90%', overflowY: 'auto' }}
          columns={[
            {
              field: 'tags',
              headerName: 'Tags',
              width: 150,
              renderCell: (params: GridRenderCellParams) => (
                <Box
                  sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, py: 0.5 }}
                >
                  {(params.value as string[]).map((tag, idx) => (
                    <Chip
                      key={idx}
                      label={tag}
                      color="primary"
                      sx={{
                        height: 20,
                        fontSize: '0.7rem',
                        '& .MuiChip-label': { px: 1 },
                      }}
                    />
                  ))}
                </Box>
              ),
            },
            { field: 'id' },
            {
              field: 'date',
              headerName: 'Last Modified',
              type: 'datetime',
              minWidth: 160,
            },
            { field: 'name', headerName: 'Name', minWidth: 200 },
            { field: 'displayName', headerName: 'Display Name', minWidth: 200 },
            { field: 'materialsIds', headerName: 'Material Ids', width: 200 },
            {
              field: 'actions',
              headerName: 'Actions',
              filterable: false,
              sortable: false,
              renderCell: (params: GridCellParams) => (
                <Box
                  onClick={(e) => {
                    e.stopPropagation() // don't select this row after clicking
                  }}
                >
                  <IconButton
                    edge="end"
                    onClick={() => {
                      handleCopyMaterialGroupClick(
                        parseInt(params.id.toString())
                      )
                    }}
                  >
                    <CopyIcon />
                  </IconButton>
                  {![9, 10].includes(parseInt(params.id.toString())) &&
                    (() => {
                      const disabledItem = usedMGroupsInDtools.find(
                        (i) => i.id === parseInt(params.id.toString())
                      )
                      return (
                        <Tooltip
                          title={disabledItem ? disabledItem.tooltip : ''}
                        >
                          <span>
                            <IconButton
                              edge="end"
                              disabled={!!disabledItem}
                              onClick={() => {
                                handleDeleteMaterialClick(
                                  parseInt(params.id.toString())
                                )
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )
                    })()}
                </Box>
              ),
              width: 150,
            },
          ]}
          // hideFooterPagination
          rows={props.materialGroups
            .map((mGroup) => {
              const relatedDtools = props.dtoolRelationsList.filter(
                (r) => r.type === 'materialGroup' && r.targetId === mGroup.id
              )
              const tags = Array.from(
                new Set(relatedDtools.flatMap((r) => r.tags))
              )

              return {
                id: mGroup.id,
                tags: tags,
                date: new Date(mGroup.date),
                name: mGroup.name,
                displayName: mGroup.displayName,
                materialsIds: mGroup.materialsIds.join(','),
              }
            })
            .filter(
              (row) =>
                selectedTags.length === 0 ||
                selectedTags.some((t) => row.tags.includes(t))
            )}
          onRowClick={(param) => {
            props.setActiveMaterialGroupId(parseInt(param.id.toString()))
          }}
          columnVisibilityModel={props.materialGroupGridVisibilityModel}
          onColumnVisibilityModelChange={(model) => {
            props.setMaterialGroupGridVisibilityModel(model)
          }}
          sortModel={props.materialGroupGridSortModel}
          onSortModelChange={(model) => {
            if (
              JSON.stringify(model) !==
              JSON.stringify(props.materialGroupGridSortModel)
            )
              props.setMaterialGroupGridSortModel(model)
          }}
          filterModel={props.materialGroupGridFilterModel}
          onFilterModelChange={(model) => {
            if (
              JSON.stringify(model) !==
              JSON.stringify(props.materialGroupGridFilterModel)
            )
              props.setMaterialGroupGridFilterModel(model)
          }}
          rowSelectionModel={props.activeMaterialGroupId}
        />
      </Box>
      {activeMGroup === undefined ? (
        <DefineMaterialGroup
          addMaterialGroup={props.addMaterialGroup}
          materials={props.materials}
          openMaterialDetails={props.openMaterialDetails}
        />
      ) : (
        <EditMaterialGroup
          mGroup={activeMGroup}
          editMaterialGroup={props.editMaterialGroup}
          materials={props.materials}
          openMaterialDetails={props.openMaterialDetails}
        />
      )}

      <Dialog open={deleteDialogOpen}>
        <DialogTitle>Delete Material Group</DialogTitle>
        <DialogContent dividers>
          <Typography>
            Are you sure you want to delete{' '}
            <b>
              "
              {
                props.materialGroups.find((mGroup) => mGroup.id === deleteID)
                  ?.name
              }
              "
            </b>{' '}
            material group?
          </Typography>
          <br />
          {props.hardwaresList.filter((h) =>
            h.materialsIds.includes(deleteID ? 'g' + deleteID : '0')
          ).length > 0 && (
            <>
              <Typography>Material Group is attached to</Typography>
              {props.hardwaresList
                .filter((h) =>
                  h.materialsIds.includes(deleteID ? 'g' + deleteID : '0')
                )
                .map((h) => (
                  <NearTooltip
                    title="Click to open hardware details"
                    placement="top"
                  >
                    <Typography
                      key={h.id}
                      component="li"
                      style={{ cursor: 'pointer', width: 'fit-content' }}
                      onClick={() => {
                        props.openHardwareDetails(h.id)
                      }}
                    >
                      {h.name}
                    </Typography>
                  </NearTooltip>
                ))}
              <br />
              <Typography>
                Remove material group from hardwares to delete it
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteDialogDeleteClick}
            color="secondary"
            disabled={
              props.hardwaresList.filter((h) =>
                h.materialsIds.includes(deleteID ? 'g' + deleteID : '0')
              ).length > 0
            }
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

const mapStateToProps = (state: StateType) => ({
  materials: state.itemReducer.materials,
  materialGroups: state.itemReducer.materialGroups,
  hardwaresList: state.itemReducer.hardwaresList,
  materialGroupGridVisibilityModel:
    state.appReducer.materialGroupGrid.visibilityModel,
  materialGroupGridSortModel: state.appReducer.materialGroupGrid.sortModel,
  materialGroupGridFilterModel: state.appReducer.materialGroupGrid.filterModel,
  activeMaterialGroupId: state.appReducer.activeMaterialGroupId,
  dtoolRelationsList: state.appReducer.dtoolRelationsList,
})

export default connect(mapStateToProps, {
  addMaterialGroup,
  editMaterialGroup,
  deleteMaterialGroup,
  copyMaterialGroup,
  setMaterialGroupGridVisibilityModel:
    appActions.setMaterialGroupGridVisibilityModel,
  setMaterialGroupGridSortModel: appActions.setMaterialGroupGridSortModel,
  setMaterialGroupGridFilterModel: appActions.setMaterialGroupGridFilterModel,
  setActiveMaterialGroupId: appActions.setActiveMaterialGroupId,
  openMaterialDetails: appActions.openMaterialDetails,
  openHardwareDetails: appActions.openHardwareDetails,
})(MaterialGroupTab)
